import "./style.css";
import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  Row,
  Table,
  Col,
  Stack,
} from "react-bootstrap";
import { Audit } from "../../models/Audit";
import AuditService, { PagedResult } from "../../services/audit.service";
import { ModalNewAudit } from "./ModalNewAudit";
import DeleteButton from "../../Helpers/deleteButton";
import useUser from "../App/useUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArchive,
  faBackwardStep,
  faBoxArchive,
  faEdit,
  faForwardStep,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import ArchiveButton from "../../Helpers/archiveButton";
import ModalArchivedAudits from "./ModalArchivedAudits/ModalArchivedAudits";

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Type guard para detectar si es PagedResult o Array
function isPagedResult<T>(
  response: T[] | PagedResult<T>,
): response is PagedResult<T> {
  return (response as PagedResult<T>).data !== undefined;
}

// Debounce hook para búsqueda
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ============================================
// COMPONENT
// ============================================

type Props = { userId: number };
type Notification = {
  show: boolean;
  message: string;
  title: string;
  variant: "success" | "danger";
};

export const Audits = (props: Props) => {
  // Estados principales
  const [audits, setAudits] = useState<Audit[]>([]);
  const [showOnlyCompleted, setShowOnlyCompleted] = useState<boolean>(false);
  const [modalShow, setModalShow] = useState(false);
  const { user, isLoading } = useUser();
  const [notification, setNotification] = useState<Notification | null>(null);
  const [showArchivedModal, setShowArchivedModal] = useState(false);

  // Estados de búsqueda y paginación
  const [filterText, setFilterText] = useState("");
  const debouncedSearchText = useDebounce(filterText, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // Default: 50 items por página

  // Estados de paginación del servidor
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  // Cargar auditorías con paginación del servidor
  const loadAudits = useCallback(async () => {
    if (!user) return setAudits([]);

    setLoading(true);
    try {
      let result: Audit[] | PagedResult<Audit>;

      if (user.role === "Administrador") {
        result = await AuditService.getAllQuestionaries(
          showOnlyCompleted,
          currentPage,
          itemsPerPage,
          false, // includeArchived
          debouncedSearchText,
        );
      } else if (user.role === "Auditor") {
        result = await AuditService.getQuestionariesByUserId(
          props.userId,
          showOnlyCompleted,
          currentPage,
          itemsPerPage,
          false, // includeArchived
          debouncedSearchText,
        );
      } else {
        setAudits([]);
        setLoading(false);
        return;
      }

      // Detectar tipo de respuesta usando type guard
      if (isPagedResult(result)) {
        // Es PagedResult - modo optimizado
        setAudits(result.data);
        setTotalRecords(result.totalRecords);
        setTotalPages(result.totalPages);
      } else {
        // Es Array - modo legacy (fallback)
        setAudits(result);
        setTotalRecords(result.length);
        setTotalPages(Math.ceil(result.length / itemsPerPage));
      }
    } catch (error) {
      console.error("Failed to load audits:", error);
      setNotification({
        show: true,
        variant: "danger",
        title: "Error de Carga",
        message: "No se pudieron cargar las auditorías.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    user,
    props.userId,
    showOnlyCompleted,
    currentPage,
    itemsPerPage,
    debouncedSearchText,
  ]);

  // Cargar datos cuando cambian las dependencias
  useEffect(() => {
    if (!isLoading) loadAudits();
  }, [isLoading, loadAudits]);

  // Resetear a página 1 cuando cambia el filtro, completados o items per page
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText, showOnlyCompleted, itemsPerPage]);

  const onSave = async (newAudit: Audit) => {
    try {
      const response = await AuditService.saveQuestionary(newAudit);
      if (response.ok) {
        setNotification({
          show: true,
          variant: "success",
          title: "Éxito",
          message: "La auditoría ha sido guardada correctamente.",
        });
        await loadAudits(); // Recargar lista
      } else {
        throw new Error("Server error: Failed to save audit");
      }
    } catch (e) {
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: "No se pudo guardar la auditoría.",
      });
    } finally {
      setModalShow(false);
    }
  };

  const onDelete = async (id: number) => {
    try {
      const res = await AuditService.deleteAudit(id);
      if (res.ok) {
        setNotification({
          show: true,
          variant: "success",
          title: "Éxito",
          message: "Auditoría eliminada correctamente.",
        });
        await loadAudits(); // Recargar lista
      } else throw new Error("Server error: Failed to delete audit");
    } catch (e) {
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: "No se pudo eliminar el registro.",
      });
    }
  };

  const setAuditIdForNavigation = (id: number) => {
    localStorage.setItem("auditId", id.toString());
  };

  const FormattedDate = ({
    isoDateString,
  }: {
    isoDateString: string | undefined;
  }) => {
    if (isoDateString === undefined) return <span>N/A</span>;
    const date = new Date(isoDateString);

    const options = {
      year: "numeric",
      month: "2-digit" as "2-digit",
      day: "2-digit" as "2-digit",
    } as const;

    const formattedDate = new Intl.DateTimeFormat("es-ES", options).format(
      date,
    );

    return <span>{formattedDate}</span>;
  };

  const handleArchived = async (audit: Audit) => {
    const response = await AuditService.archived(audit);

    if (response.ok) {
      setNotification({
        show: true,
        variant: "success",
        title: "Éxito",
        message: audit.isArchived
          ? "Auditoría restaurada correctamente."
          : "Auditoría archivada correctamente.",
      });
      await loadAudits(); // Recargar lista
      return;
    } else {
      const errorMessage = await response.text();
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: errorMessage || "Error al archivar auditoría.",
      });
    }
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Helper para obtener nombres compatibles con DTOs y entidades
  const getCompanyName = (audit: any) => {
    return audit.companyName || audit.company?.name || "";
  };

  const getPolicyTypeDesc = (audit: any) => {
    return audit.policyTypeDesc || audit.policyType?.policyDesc || "";
  };

  const getUserName = (audit: any) => {
    return audit.userName || audit.user?.name || "";
  };

  // Generar items de paginación
  const renderPaginationItems = () => {
    if (totalPages === 0) return null;

    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      items.push(
        <Button
          key={1}
          variant={currentPage === 1 ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => setCurrentPage(1)}
        >
          1
        </Button>,
      );
      if (startPage > 2) {
        items.push(
          <span key="ellipsis-start" className="mx-1">
            ...
          </span>,
        );
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Button
          key={page}
          variant={currentPage === page ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => setCurrentPage(page)}
          className="mx-1"
        >
          {page}
        </Button>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="ellipsis-end" className="mx-1">
            ...
          </span>,
        );
      }
      items.push(
        <Button
          key={totalPages}
          variant={currentPage === totalPages ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Button>,
      );
    }

    return items;
  };

  // Calcular rango de registros mostrados
  const startRecord =
    totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <>
      <div className="notification-container">
        {notification && (
          <Alert
            variant={notification.variant}
            onClose={() => setNotification(null)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{notification.title}</Alert.Heading>
            {notification.message}
          </Alert>
        )}
      </div>

      <ModalNewAudit
        show={modalShow}
        onHide={() => setModalShow(false)}
        onSave={onSave}
        userId={props.userId}
      />
      <Container className="pt-3">
        <Card>
          <Card.Body>
            <Card.Title>Auditorías</Card.Title>
            <hr />
            <Row className="mb-3 g-3 align-items-center">
              <Col md={5}>
                <Form.Control
                  type="text"
                  placeholder="Buscar..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  disabled={loading}
                />
              </Col>

              {user?.role === "Administrador" && (
                <Col
                  md={2}
                  className="d-flex justify-content-md-start align-items-center"
                >
                  <Stack direction="horizontal" gap={3}>
                    <Button
                      onClick={() => setShowArchivedModal(true)}
                      variant="primary"
                      className="d-flex align-items-center"
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faArchive} className="white" />
                      <span className="ms-2">Archivados</span>
                    </Button>
                  </Stack>
                </Col>
              )}
              <Col
                md={user?.role === "Administrador" ? 5 : 7}
                className="d-flex justify-content-md-end align-items-center"
              >
                <Stack direction="horizontal" gap={3}>
                  <Form.Check
                    type="switch"
                    id="completeSwitch"
                    label="Solo Completados"
                    checked={showOnlyCompleted}
                    onChange={(e) => setShowOnlyCompleted(e.target.checked)}
                    disabled={loading}
                  />
                  {(user?.role === "Administrador" ||
                    user?.role === "Auditor") && (
                    <Button
                      variant="primary"
                      onClick={() => setModalShow(true)}
                      className="d-flex align-items-center"
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faPlus} className="white" />
                      <span className="ms-2">Nueva Auditoría</span>
                    </Button>
                  )}
                </Stack>
              </Col>
            </Row>

            {/* Selector de items y contador */}
            <Row className="mb-2 align-items-center">
              <Col md={6}>
                {!loading && totalRecords > 0 && (
                  <div className="text-muted small">
                    Mostrando {startRecord} a {endRecord} de {totalRecords}{" "}
                    registros
                  </div>
                )}
              </Col>
              <Col md={6} className="text-end">
                <Form.Group
                  as={Row}
                  className="align-items-center justify-content-end d-inline-flex"
                >
                  <Form.Label
                    column
                    sm="auto"
                    className="small text-muted me-2"
                  >
                    Mostrar:
                  </Form.Label>
                  <Col sm="auto">
                    <Form.Select
                      size="sm"
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      style={{ width: "auto" }}
                      disabled={loading}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Form.Select>
                  </Col>
                  <Form.Label
                    column
                    sm="auto"
                    className="small text-muted ms-2"
                  >
                    por página
                  </Form.Label>
                </Form.Group>
              </Col>
            </Row>

            <Table striped bordered hover responsive>
              <thead>
                <tr className="text-center">
                  <th>Id</th>
                  <th>Compañía</th>
                  <th>No. Póliza</th>
                  <th>Tipo de Póliza</th>
                  <th>Fecha creación</th>
                  <th>Auditor</th>
                  <th>Completado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {audits.map((audit) => (
                  <tr key={audit.id}>
                    <td className="text-center">{audit.id}</td>
                    <td>{getCompanyName(audit)}</td>
                    <td className="text-center">{audit.policyNumber}</td>
                    <td>{getPolicyTypeDesc(audit)}</td>
                    <td className="text-center">
                      <FormattedDate
                        isoDateString={audit.creationDate?.toString()}
                      />
                    </td>
                    <td>{getUserName(audit)}</td>
                    <td className="text-center">
                      <span
                        className={`badge rounded-pill ${
                          audit.statusId === 3
                            ? "bg-success"
                            : "bg-warning text-dark"
                        }`}
                      >
                        {audit.statusId === 3 ? "Sí" : "No"}
                      </span>
                    </td>
                    <td className="text-center">
                      <Stack
                        direction="horizontal"
                        gap={2}
                        className="justify-content-center"
                      >
                        <Button
                          variant="primary"
                          size="sm"
                          active
                          title="Seleccionar"
                          onClick={() => setAuditIdForNavigation(audit.id!)}
                          href="/FillAudit"
                        >
                          <FontAwesomeIcon icon={faEdit} className="white" />
                        </Button>
                        {user?.role === "Administrador" && (
                          <>
                            <ArchiveButton
                              title="Archivar"
                              description="¿Estás seguro que deseas archivar el registro?"
                              iconButton={faBoxArchive}
                              onClick={() => handleArchived(audit)}
                            />
                            <DeleteButton onClick={() => onDelete(audit.id!)} />
                          </>
                        )}
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3 align-items-center gap-2">
                <Button
                  variant={currentPage === 1 ? "secondary" : "primary"}
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                >
                  <FontAwesomeIcon icon={faBackwardStep} />
                </Button>

                {renderPaginationItems()}

                <Button
                  variant={currentPage === totalPages ? "secondary" : "primary"}
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                >
                  <FontAwesomeIcon icon={faForwardStep} />
                </Button>
              </div>
            )}

            {audits.length === 0 && !loading && (
              <div className="text-center text-muted mt-3">
                No se encontraron auditorías.
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/** Modal de Archivados */}
      <ModalArchivedAudits
        onHide={() => setShowArchivedModal(false)}
        show={showArchivedModal}
        onRestore={handleArchived}
      />
    </>
  );
};
